import { InputSource, } from '@medely/types';
import { ConditionHumanizer } from './ConditionHumanizer';
import { datePiecesFromDateComparator } from './utils/dateComparators';
import { MedelyDateTime } from '@medely/date-time-tools';
const EXPIRES_SOON_DAYS = 60;
/**
 * A class that provides methods to help determine the reasons why a condition is not satisfied, and what can be done to
 * satisfy the condition
 */
export class ConditionSatisfactionHelper {
    condition;
    professionalQualifications;
    qualificationMapping;
    satisfiedConditionIds;
    includeIncompleteStatusInReviewCheck;
    constructor({ condition, professionalQualifications, satisfiedConditionIds = [], includeIncompleteStatusInReviewCheck = false, }) {
        this.condition = condition;
        this.professionalQualifications = professionalQualifications;
        this.satisfiedConditionIds = satisfiedConditionIds;
        this.includeIncompleteStatusInReviewCheck = includeIncompleteStatusInReviewCheck;
        if (!condition.payload ||
            !condition.qualifications ||
            condition.qualifications.some((qualification) => !qualification.id || !qualification.slug)) {
            throw new Error('ConditionSatisfactionHelper must be instantiated with a condition with its payload and nested qualifications, including the qualification ids and slugs');
        }
        if (professionalQualifications.some((pq) => !pq.qualification?.id || !pq.qualification?.slug)) {
            throw new Error('ConditionSatisfactionHelper must be instantiated with an array of the professional qualifications, including the nested qualification with its id and slug');
        }
        if (professionalQualifications.some((pq) => pq.professional_qualification_attributes?.some((pqa) => !pqa.qualification_attribute?.slug))) {
            throw new Error('ConditionSatisfactionHelper must be instantiated with an array of the professional qualifications, including the nested pqas with qualification_attributes with its slug');
        }
        this.qualificationMapping = this.buildQualificationMapping();
    }
    /**
     * Method for seeing all of the possible ways a condition can be satisfied
     * @returns An array of SatisfactionOptions
     */
    get satisfactionOptions() {
        const options = [];
        const symmetricDescriptionsMap = new Map([['gte', {}]]);
        this.condition.payload.forEach((orCondition) => {
            const qualifications = orCondition.map((andCheck) => {
                const slug = Object.keys(andCheck)[0];
                const existingPqs = this.professionalQualifications.filter((pq) => pq.qualification.slug === slug);
                const satisfyingPqs = existingPqs.filter((existingPq) => this.pQWillSatisfyOption(existingPq, orCondition));
                const isMissing = satisfyingPqs.length === 0;
                const description = new ConditionHumanizer(this.condition).buildAndConditionExplanation(andCheck, { buildEmptyAttributeExplanation: false });
                const isGte = Object.keys(andCheck).some((key) => Object.values(andCheck[key]).some((value) => typeof value === 'object' && value['gte'] !== undefined));
                if (isGte && description) {
                    symmetricDescriptionsMap.set('gte', {
                        ...symmetricDescriptionsMap.get('gte'),
                        [slug]: description,
                    });
                }
                return {
                    qualification: this.qualificationMapping[slug],
                    satisfyingProfessionalQualifications: satisfyingPqs,
                    isMissing,
                    description,
                };
            });
            const description = this.buildDescription(orCondition);
            options.push({ orCondition, qualifications, description });
        });
        options.forEach((option) => {
            option.qualifications.forEach((qualification) => {
                if (!qualification.description &&
                    symmetricDescriptionsMap.get('gte')?.[qualification.qualification.slug]) {
                    qualification.description = 'Completed at any date';
                }
            });
        });
        return options;
    }
    /**
     * Method for seeing only available ways a condition can be satisfied by a professional
     * We do use admin_view_only=false and status=active criteria to resolve them
     * @returns An array of SatisfactionOptions
     */
    get availableSatisfactionOptions() {
        return this.satisfactionOptions.filter((option) => {
            const hasAdminOrInactive = option.qualifications.some(({ qualification }) => qualification.admin_view_only || qualification.status !== 'active');
            return !hasAdminOrInactive;
        });
    }
    /**
     * Method for seeing only available ways a condition can be satisfied by an agency
     * We use status=active criteria to resolve them
     * @returns An array of SatisfactionOptions
     */
    get availableAgencySatisfactionOptions() {
        return this.satisfactionOptions.filter((option) => {
            const hasAvailableQualifications = option.qualifications.some(({ qualification }) => qualification.status === 'active' &&
                [InputSource.User, InputSource.Agency].includes(qualification.input_source));
            return hasAvailableQualifications;
        });
    }
    /**
     * returns a boolean that tells us if the condition is already satisfied or not
     */
    get isSatisfied() {
        return this.satisfiedConditionIds.includes(this.condition.id);
    }
    /**
     * returns a boolean that tells us if the pro has already done everything they need to do,
     * and are now just awaiting review from Medely
     */
    get isAwaitingReview() {
        return this.includeIncompleteStatusInReviewCheck
            ? this.unsatisfiedDueToStatuses(['review', 'pending', 'incomplete'])
            : this.unsatisfiedDueToStatuses(['review', 'pending']);
    }
    /**
     * returns a boolean that tells us if the pro has already uploaded every pq needed to satisfy the
     * condition, but one or more pq's are in status rejected
     */
    get unsatisfiedDueToRejectedPq() {
        return this.unsatisfiedDueToStatuses(['rejected']);
    }
    /**
     * returns a boolean that tells us if the pro has already uploaded every pq needed to satisfy the
     * condition, but one or more pq's are in status expired
     */
    get unsatisfiedDueToExpiredPq() {
        return this.unsatisfiedDueToStatuses(['expired']);
    }
    unsatisfiedDueToStatuses(statuses) {
        if (this.satisfiedConditionIds.includes(this.condition.id)) {
            return false;
        }
        // need to check each possible way of satisfying the condition, if at least one will satisfy an or option, we're good
        let atLeastOneReachableOption = false;
        for (const satisfactionOption of this.satisfactionOptions) {
            const relatedPqs = [];
            satisfactionOption.qualifications.forEach((qualification) => {
                const existingPqs = this.professionalQualifications.filter((pq) => pq.qualification.id === qualification.qualification.id);
                relatedPqs.push(...existingPqs);
            });
            // if every related pq for the condition exists, and they are all either in status review or approved,
            // there's nothing left for the pro to do, so the condition is just awaiting review
            let optionIsReachable = true;
            for (const qualification of satisfactionOption.qualifications) {
                const qualificationPqs = relatedPqs.filter((pq) => pq.qualification.id === qualification.qualification.id);
                const qualificationIsReachable = qualificationPqs.some((pq) => this.pQWillSatisfyOption(pq, satisfactionOption.orCondition) &&
                    ['approved', ...statuses].includes(pq?.status));
                if (!qualificationIsReachable) {
                    optionIsReachable = false;
                }
            }
            if (optionIsReachable) {
                atLeastOneReachableOption = true;
                return true;
            }
        }
        return atLeastOneReachableOption;
    }
    /**
     * returns the date that a pq will cause a condition option to expire or null if the pq will not expire within 60 days
     */
    expiringPq(pq, option) {
        if (pq.professional_qualification_attributes.some((pqa) => pqa.qualification_attribute.field_type === 'expiration_date')) {
            return this.expirationDateIsWithin60Days(pq);
        }
        const dateComparator = this.matchingDateComparator(pq, option.orCondition);
        if (dateComparator) {
            return this.dateIsInvalidWithin60DaysComp(pq, dateComparator);
        }
        return null;
    }
    expirationDateIsWithin60Days(pq) {
        // expiration date: check if the expiration date is within 60 days
        const expirationDatePqa = pq.professional_qualification_attributes.find((pqa) => pqa.qualification_attribute.field_type === 'expiration_date');
        const expiresSoonDate = MedelyDateTime.now({ tz: 'UTC' }).plus({ days: EXPIRES_SOON_DAYS });
        if (new MedelyDateTime(expirationDatePqa.value, { tz: 'UTC' }).isBefore(expiresSoonDate)) {
            return expirationDatePqa.value;
        }
        return null;
    }
    matchingDateComparator(pq, orCondition) {
        return orCondition.reduce((matchingComparator, andCheck) => {
            const qualificationSlug = Object.keys(andCheck)[0];
            if (qualificationSlug === pq.qualification.slug) {
                const qualificationObject = andCheck[qualificationSlug];
                const comparator = Object.values(qualificationObject)[0];
                if (typeof comparator === 'object' && Object.keys(comparator)[0] === 'gte') {
                    return qualificationObject;
                }
            }
            return matchingComparator;
        }, null);
    }
    dateIsInvalidWithin60DaysComp(pq, dateComparator) {
        // Check if the value of the pqa is within 60 days of no longer being within the date comparator range
        const datePqa = pq.professional_qualification_attributes.find((pqa) => pqa.qualification_attribute.slug === Object.keys(dateComparator)[0]);
        const targetValue = Object.values(dateComparator)[0];
        if (datePqa && targetValue['gte']) {
            const comparisonObject = datePiecesFromDateComparator(targetValue['gte']);
            const targetPqaDateTime = new MedelyDateTime(datePqa.value, { tz: 'UTC' });
            const adjustedPqaTime = targetPqaDateTime.minus({ days: EXPIRES_SOON_DAYS });
            const earliestSatisfyingDate = MedelyDateTime.now({ tz: 'UTC' }).minus(comparisonObject);
            if (adjustedPqaTime.isBefore(earliestSatisfyingDate)) {
                return targetPqaDateTime.plus(comparisonObject).toISODate();
            }
        }
        return null;
    }
    buildQualificationMapping() {
        const qualificationMapping = {};
        this.condition.qualifications.forEach((qualification) => {
            qualificationMapping[qualification.slug] = qualification;
        });
        return qualificationMapping;
    }
    buildDescription(orCondition) {
        const humanizer = new ConditionHumanizer(this.condition);
        return humanizer.buildConditionExplanation(orCondition);
    }
    pQWillSatisfyOption(pq, orCondition) {
        // this just needs to check that the pq we're looking at actually will meet the requirements
        // defined for it on the orCondition
        const targetQualificationAttributes = orCondition.find((andCheck) => Object.keys(andCheck)[0] === pq.qualification.slug)?.[pq.qualification.slug];
        if (!targetQualificationAttributes) {
            return false;
        }
        for (const qualificationAttributeSlug in targetQualificationAttributes) {
            const pqa = pq.professional_qualification_attributes.find((attribute) => attribute.qualification_attribute.slug === qualificationAttributeSlug);
            if (!pqa) {
                return false;
            }
            if (!this.checkPqaAgainstCondition(pqa, targetQualificationAttributes[qualificationAttributeSlug])) {
                return false;
            }
        }
        return true;
    }
    checkPqaAgainstCondition(pqa, targetValue) {
        const isGreaterThanComparator = typeof targetValue === 'object' && Object.keys(targetValue)[0] === 'gte';
        if (isGreaterThanComparator) {
            const comparisonObject = datePiecesFromDateComparator(targetValue['gte']);
            const pqaDateTime = new MedelyDateTime(pqa.value, { tz: 'UTC' });
            const comparisonDateTime = MedelyDateTime.now({ tz: 'UTC' }).minus(comparisonObject);
            return pqaDateTime.isAfter(comparisonDateTime);
        }
        else if (typeof targetValue === 'boolean') {
            return String(pqa.value) === String(targetValue);
        }
        else {
            return pqa.value === targetValue;
        }
    }
}
